
import { useMemo, useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import {
  coursesQuery,
  progressScoresQuery,
  progressScoresLevelsQuery,
} from '../api';

import { navigation } from '../data';
import { gameTypes } from './data';
import SubPagesLayout from '../../../UI/layout/subPages';

import './styles.css';
import Table from '../../../UI/table';

const formatAccuracy = (value) =>
  typeof value === 'number' && !Number.isNaN(value) ? `${(value * 100).toFixed(2)}%` : '--';

// Level breakdown is capped at 3 in the backend contract
// (best_accuracy_level1 / _level2 / _level3). Update if the backend
// starts returning higher-level fields.
const LEVEL_NUMBERS = [1, 2, 3];

// Compute an average of a numeric-or-null array, ignoring nulls.
// Returns null if no valid values, so the caller can format as '--'.
const averageNonNull = (values) => {
  const nums = values.filter((v) => typeof v === 'number' && !Number.isNaN(v));
  if (!nums.length) return null;
  return nums.reduce((sum, v) => sum + v, 0) / nums.length;
};

const buildDateString = () => new Date().toISOString().split('T')[0];

export default function InstructorScores() {
  const navigate = useNavigate();

  // Single dedicated endpoint replaces the old fan-out of courseMembers +
  // generalInfo + consolidatedScores + client-side aggregation. Courses
  // call is a tiny hop to resolve the course id the aggregated endpoint
  // requires, and hits the shared react-query cache alongside the
  // sibling progress page.
  const { data: courses } = useQuery(coursesQuery());
  const courseId = courses?.[0]?.id;

  const { data: progressScores } = useQuery(progressScoresQuery({ courseId }));

  // Lazy fetch for the level-scores CSV. Only fires once the user
  // clicks the "Download level scores" button, so the extra request
  // doesn't run on initial page load. A ref tracks whether the user's
  // click has an outstanding download waiting for data; when the query
  // resolves and the ref is set, the useEffect below fires the actual
  // download.
  const [levelScoresRequested, setLevelScoresRequested] = useState(false);
  const levelDownloadPending = useRef(false);
  const { data: progressScoresLevels, isFetching: isLevelScoresFetching } = useQuery({
    ...progressScoresLevelsQuery({ courseId }),
    enabled: !!courseId && levelScoresRequested,
  });

  const { headers, rows, csvHeaders, csvRows } = useMemo(() => {
    if (!progressScores) return { headers: [], rows: [], csvHeaders: [], csvRows: [] };

    const games = progressScores.games || [];
    const students = progressScores.students || [];

    const gameColumnLabels = games.map(
      game => `${game.name}<br><span class="thin">${gameTypes[game.game_type]}</span>`
    );

    const tableHeaders = ['Student\'s Name', 'Overall', ...gameColumnLabels];
    const csvHeadersArr = ['First Name', 'Last Name', 'Email', 'Overall', ...gameColumnLabels];

    const tableRowsArr = [];
    const csvRowsArr = [];

    students.forEach(student => {
      const perGame = student.per_game || {};

      const perGameAccuracy = games.map(game => {
        const entry = perGame[String(game.id)];
        return typeof entry?.best_accuracy === 'number' ? entry.best_accuracy : null;
      });

      const numericScores = perGameAccuracy.filter(v => v != null);
      const overallAccuracy = numericScores.length
        ? formatAccuracy(numericScores.reduce((sum, v) => sum + v, 0) / numericScores.length)
        : '--';

      const perGameCells = perGameAccuracy.map(v => formatAccuracy(v));

      tableRowsArr.push([
        `${student.first_name}${student.last_name ? ` ${student.last_name}` : ''}`,
        overallAccuracy,
        ...perGameCells,
      ]);

      csvRowsArr.push([
        student.first_name || '',
        student.last_name || '',
        student.email || '',
        overallAccuracy,
        ...perGameCells,
      ]);
    });

    return {
      headers: tableHeaders,
      rows: tableRowsArr,
      csvHeaders: csvHeadersArr,
      csvRows: csvRowsArr,
    };
  }, [progressScores]);

  // Build the level-scores CSV. Column existence rules:
  // - Include "Average Level N" if ANY student has a non-null score
  //   for level N on ANY game.
  // - Include "<Game name> Level N" if ANY student has a non-null
  //   score for level N on THAT specific game.
  // "Average Level N" for a student = mean of that student's level N
  // scores across the games where they have a value.
  const levelCsvConfig = useMemo(() => {
    if (!progressScoresLevels) return null;

    const games = progressScoresLevels.games || [];
    const students = progressScoresLevels.students || [];

    // Which levels have any data at all (used for the Average columns)?
    const activeAverageLevels = LEVEL_NUMBERS.filter((level) =>
      students.some((student) => {
        const perGame = student.per_game || {};
        return games.some((g) => {
          const entry = perGame[String(g.id)];
          const val = entry?.[`best_accuracy_level${level}`];
          return typeof val === 'number' && !Number.isNaN(val);
        });
      })
    );

    // Which levels have data per game (used for the assignment columns)?
    const activeLevelsByGameId = new Map();
    games.forEach((g) => {
      const levels = LEVEL_NUMBERS.filter((level) =>
        students.some((student) => {
          const entry = (student.per_game || {})[String(g.id)];
          const val = entry?.[`best_accuracy_level${level}`];
          return typeof val === 'number' && !Number.isNaN(val);
        })
      );
      activeLevelsByGameId.set(g.id, levels);
    });

    // Two-row header. Row 1 carries the top-level labels — First
    // Name / Last Name / Email each stand alone; "Averages" heads the
    // aggregate block; each assignment name heads its own block. The
    // group label is REPEATED across every column of its group so
    // strict CSV parsers can associate each Row 2 sub-header with a
    // Row 1 group directly above (no visual-only merging). Row 2
    // carries the "Level N" sub-labels; blank under the identity
    // columns.
    const headerRow1 = [
      'First Name', 'Last Name', 'Email',
      ...Array(activeAverageLevels.length).fill('Averages'),
      ...games.flatMap((g) => {
        const levels = activeLevelsByGameId.get(g.id);
        return Array(levels.length).fill(g.name);
      }),
    ];

    const headerRow2 = [
      '', '', '',
      ...activeAverageLevels.map((l) => `Level ${l}`),
      ...games.flatMap((g) =>
        activeLevelsByGameId.get(g.id).map((l) => `Level ${l}`)
      ),
    ];

    const rows = students.map((student) => {
      const perGame = student.per_game || {};

      const averageCells = activeAverageLevels.map((level) => {
        const values = games.map((g) => {
          const entry = perGame[String(g.id)];
          const val = entry?.[`best_accuracy_level${level}`];
          return typeof val === 'number' ? val : null;
        });
        return formatAccuracy(averageNonNull(values));
      });

      const perGameCells = games.flatMap((g) => {
        const levels = activeLevelsByGameId.get(g.id);
        return levels.map((level) => {
          const entry = perGame[String(g.id)];
          const val = entry?.[`best_accuracy_level${level}`];
          return formatAccuracy(typeof val === 'number' ? val : null);
        });
      });

      return [
        student.first_name || '',
        student.last_name || '',
        student.email || '',
        ...averageCells,
        ...perGameCells,
      ];
    });

    return {
      headerRows: [headerRow1, headerRow2],
      rows,
      filename: `instructor_level_scores_${buildDateString()}.csv`,
    };
  }, [progressScoresLevels]);

  // Once level-scores data lands AND the user has an outstanding
  // download click waiting, actually build the CSV and trigger the
  // browser download. The download itself is synchronous once the
  // data is ready.
  useEffect(() => {
    if (!levelDownloadPending.current || !levelCsvConfig) return;
    const { headerRows, rows: r, filename } = levelCsvConfig;
    if (!headerRows?.length || !r.length) return;

    const serializeRow = (row) =>
      row
        .map((cell) =>
          `"${String(cell)
            .replace(/<br>/g, ' - ')
            .replace(/<[^>]*>/g, '')
            .replace(/"/g, '""')}"`
        )
        .join(',');

    const headerLines = headerRows.map(serializeRow).join('\n');
    const csvBody = r.map(serializeRow).join('\n');
    const csv = `${headerLines}\n${csvBody}`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    levelDownloadPending.current = false;
  }, [levelCsvConfig]);

  const handleDownloadLevelScores = () => {
    // Kick off the lazy fetch on first click (React Query caches the
    // result, so subsequent clicks re-trigger the download from cache).
    levelDownloadPending.current = true;
    if (!levelScoresRequested) {
      setLevelScoresRequested(true);
      return;
    }
    // Already fetched — the useEffect above will fire immediately
    // after this render because levelDownloadPending is now true.
  };

  const downloads = useMemo(() => ([
    {
      label: 'Download overall scores',
      headers: csvHeaders,
      rows: csvRows,
      filename: `instructor_scores_${buildDateString()}.csv`,
    },
    {
      label: 'Download level scores',
      onClick: handleDownloadLevelScores,
      isLoading: levelScoresRequested && (isLevelScoresFetching || !progressScoresLevels),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [csvHeaders, csvRows, levelScoresRequested, isLevelScoresFetching, progressScoresLevels]);

  const handlePrevButtonClick = () => {
    navigate('/instructor/insights/progress-scores/progress');
  };

  return (
    <SubPagesLayout
      navigation={navigation}
      onFirstButtonClick={handlePrevButtonClick}
      currentPage={1}
      pagesQuantity={1}
      contentClassName="decision-insights-content-container"
    >
      <h1>Insights</h1>
      <h2>Scores | Accuracy Score</h2>
      <p>The scores below represent students' best scores on each assignment. For each assignment, they have up to three tries to improve their performance.</p>

      <Table
        headers={headers}
        rows={rows}
        downloads={downloads}
        rowsPerPageOptions={[50, 75, 100]}
      />
    </SubPagesLayout>
  );
}
